import { BRAND } from "constants/brand";
import { useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Ring } from "@uiball/loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faEye,
    faEyeSlash,
    faStar as faStarSolid,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import { Toast } from "primereact/toast";

import { useGetAdminReview, useSetReviewStatus } from "hooks/useReviews";
import { formatDateTime } from "utils/dateFormat";

const TARGET_TYPE_LABELS = {
    product: "منتج",
    vendor: "مطعم",
    delivery: "سائق",
};

const Stars = ({ value }) => {
    const filled = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
    return (
        <span aria-label={`${filled} من 5 نجوم`} style={{ color: "#f5a623", fontSize: "24px" }}>
            {[1, 2, 3, 4, 5].map((i) => (
                <FontAwesomeIcon
                    key={i}
                    icon={i <= filled ? faStarSolid : faStarRegular}
                    style={{ marginInlineEnd: "4px" }}
                />
            ))}
        </span>
    );
};

const Field = ({ label, value }) => (
    <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#777", fontSize: "13px", marginBottom: "4px" }}>{label}</div>
        <div style={{ fontSize: "15px" }}>{value ?? "—"}</div>
    </div>
);

/**
 * Single-review detail page (`/reviews/:id`). Read-only view of the
 * review row + a hide/unhide button. The row is preserved when hidden
 * so the action is reversible.
 *
 * Linking off to /orders/:id (the anchor order) is intentionally NOT
 * here because the existing orders page is filtered by URL params and
 * doesn't take a single-ID jump cleanly — admins click "افتح الطلب"
 * by visiting /orders and filtering, or open the user record.
 */
const ReviewDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { review, loading } = useGetAdminReview(id);
    const setStatusMutation = useSetReviewStatus();
    const toast = useRef(null);

    const notify = (severity, detail) => {
        toast.current?.show({ severity, detail, life: 3000 });
    };

    const toggleStatus = async () => {
        if (!review) return;
        const next = review.status === "visible" ? "hidden" : "visible";
        try {
            await setStatusMutation.mutateAsync({ id: review.id, status: next });
            notify("success", next === "hidden" ? "تم إخفاء التقييم" : "تم إظهار التقييم");
        } catch (err) {
            notify("error", err?.response?.data?.message || "فشل تحديث الحالة");
        }
    };

    if (loading) {
        return (
            <div className="loadingSpinner">
                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
            </div>
        );
    }

    if (!review) {
        return (
            <div style={{ padding: "40px", textAlign: "center" }}>
                <p>التقييم غير موجود</p>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                >
                    العودة
                </button>
            </div>
        );
    }

    return (
        <div className="items" style={{ padding: "20px" }}>
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate(-1)}
                    >
                        <FontAwesomeIcon icon={faArrowRight} /> العودة
                    </button>
                    <h2 style={{ margin: 0 }}>تقييم #{review.id}</h2>
                </div>
                <button
                    type="button"
                    className={`btn ${
                        review.status === "visible" ? "btn-outline-danger" : "btn-outline-success"
                    }`}
                    onClick={toggleStatus}
                    disabled={setStatusMutation.isLoading}
                >
                    <FontAwesomeIcon icon={review.status === "visible" ? faEyeSlash : faEye} />{" "}
                    {review.status === "visible" ? "إخفاء التقييم" : "إظهار التقييم"}
                </button>
            </div>

            <div className="surface-card" style={{ padding: "24px" }}>
                <div style={{ marginBottom: "20px" }}>
                    <Stars value={review.rating} />
                    <span style={{ marginInlineStart: "12px", fontWeight: 600 }}>
                        {review.rating} من 5
                    </span>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px 24px",
                        marginBottom: "16px",
                    }}
                >
                    <Field
                        label="الحالة"
                        value={
                            <span
                                style={{
                                    color: review.status === "hidden" ? "#cc0000" : BRAND,
                                    fontWeight: 600,
                                }}
                            >
                                {review.status === "hidden" ? "مخفي" : "ظاهر"}
                            </span>
                        }
                    />
                    <Field label="التاريخ" value={formatDateTime(review.createdAt)} />
                    <Field label="آخر تعديل" value={formatDateTime(review.updatedAt)} />
                    <Field
                        label="الهدف"
                        value={(() => {
                            const type = TARGET_TYPE_LABELS[review.targetType] ?? review.targetType;
                            const name = review.target?.name;
                            if (!name) return `${type} #${review.targetId}`;
                            if (review.targetType === "product" && review.target?.vendorName) {
                                return `${type}: ${name} — ${review.target.vendorName} (#${review.targetId})`;
                            }
                            if (review.targetType === "delivery" && review.target?.phone) {
                                return `${type}: ${name} — ${review.target.phone} (#${review.targetId})`;
                            }
                            return `${type}: ${name} (#${review.targetId})`;
                        })()}
                    />
                    <Field
                        label="رقم الطلب"
                        value={
                            <Link
                                to={`/orders/${review.orderId}`}
                                style={{ color: BRAND, textDecoration: "underline" }}
                            >
                                #{review.orderId}
                            </Link>
                        }
                    />
                    <Field
                        label="المُقَيِّم"
                        value={
                            review.user
                                ? `${review.user.name ?? "—"} (${review.user.phone ?? "—"})`
                                : `#${review.userId}`
                        }
                    />
                </div>

                <Field
                    label="التعليق"
                    value={
                        review.comment ? (
                            <div
                                className="surface-inset"
                                style={{
                                    padding: "12px",
                                    borderRadius: "6px",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {review.comment}
                            </div>
                        ) : (
                            <span style={{ color: "#999" }}>(لا يوجد تعليق)</span>
                        )
                    }
                />
            </div>
        </div>
    );
};

export default ReviewDetail;
